"use client"

import { useEffect, useState } from "react"
import { useWallet } from "@/lib/wallet-provider"

export interface LeaderboardEntry {
  pubkey: string
  bestScore: number
  runs: number
  updatedAt: string
}

/**
 * Global leaderboard standings, plus the connected wallet's rank within them.
 *
 * Shared by the title-screen modal and the menu's Records section, which used to
 * carry their own copy of this fetch. Two copies meant two chances to drift on
 * the limit, the error text, or how "my rank" is derived.
 *
 * `enabled` lets a caller inside a closed modal skip the request entirely.
 */
export function useLeaderboard(enabled = true, limit = 50) {
  const { identityKey } = useWallet()
  const [entries, setEntries] = useState<LeaderboardEntry[]>([])
  const [isLoading, setIsLoading] = useState(enabled)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    fetch(`/api/leaderboard?limit=${limit}`)
      .then((res) => {
        if (!res.ok) throw new Error("Failed to load")
        return res.json()
      })
      .then((data) => {
        if (!cancelled) {
          setEntries(Array.isArray(data) ? data : [])
          setError(null)
        }
      })
      .catch(() => {
        if (!cancelled) setError("Could not load the leaderboard")
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [enabled, limit])

  // 0 means "not ranked" - either no wallet, or no score submitted yet
  const myRank = identityKey ? entries.findIndex((e) => e.pubkey === identityKey) + 1 : 0

  return { entries, isLoading, error, myRank, identityKey }
}
